import { Component, OnDestroy, OnInit } from "@angular/core";
import { PageEvent } from "src/app/shared/directives/paginator.component";
import { Observable, Subject, finalize, of, takeUntil } from "rxjs";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";
import { AccountService } from "src/app/shared/services/account.service";
import { UtilsService } from "src/app/shared/utils.service";
import { AllocationLog } from "../../models/allocation-log";
import { AllocationLogSearch } from "../../models/allocation-log-search";
import { DateTimeHelperService } from "src/app/shared/services/date-time-helper.service";
import { faClockRotateLeft, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-view-log-list',
    templateUrl: './view-log-list.component.html',
    styleUrls: ['./view-log-list.component.scss'],
    standalone: false
})
export class ViewLogListComponent implements OnInit, OnDestroy {
  faClockRotateLeft: any = faClockRotateLeft;
  faMagnifyingGlass: any = faMagnifyingGlass;

  allocationLogs: AllocationLog[] = [];
  displayedColumns: string[] = ['account', 'user', 'symbol', 'side', 'price', 'quantity', 'timestamp', 'allocatedBy', 'action'];

  totalRecords = 0;
  pagedElements$: Observable<AllocationLog[]>;
  private elementSearch: AllocationLogSearch;

  pageIndex = 0;
  pageSize = 25;

  loading = false;
  searchExecuted = false;
  private onDestroy$ = new Subject<void>();
  cachedClientUser: any = null;

  constructor(private accountService: AccountService, 
      private utilsService: UtilsService,
      public dateTimeHelper: DateTimeHelperService) { }

  ngOnInit(): void {
    this.accountService.searchLogExecuted$
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => { this.loading = true; }),
        map(search => this.elementSearch = search),
        filter(() => this.elementSearch.riaId > 0),
        switchMap(() => {
          return this.utilsService.fetchClientUserDetails(this.elementSearch .riaId).pipe(
            tap(user => this.cachedClientUser = user),
            catchError(err => {
              console.error('Client user fetch error:', err);
              this.cachedClientUser = null;
              return of(null);
            }),
          )
        }),
        filter(() => this.elementSearch.riaId > 0),        
        tap(() => this.fetchGroupPositionSummary())
      )
      .subscribe();
  }

  fetchGroupPositionSummary(): void {
    this.accountService
      .GetAllocationLog(this.elementSearch.riaId, this.elementSearch.fromDate, this.elementSearch.toDate)
      .pipe(
        tap((elements: AllocationLog[]) => {
          const safeElements = Array.isArray(elements) ? elements : [];
          this.allocationLogs = safeElements;
          this.totalRecords = safeElements.length;
          this.getPagedData();
          this.searchExecuted = true;
        }),
        catchError((error) => {
          console.error('Error fetching allocation log:', error);
          // Ensure UI shows the noRecordsFound template on error
          this.allocationLogs = [];
          this.totalRecords = 0;
          this.getPagedData();
          this.searchExecuted = true;
          return of([] as AllocationLog[]);
        }),
        finalize(() => this.loading = false)
      )
      .subscribe(() => {
        this.allocationLogs.forEach(el => {
          this.processElement(el).subscribe(); // Process each element
        });
      });
  }

  processElement(element: AllocationLog): Observable<any> {
    if (element.bowiBrokerId > 0) {
      return this.utilsService.fetchBrokerUserDetails(element.bowiBrokerId)
        .pipe(
          tap((user) => {
            const userType = "Broker";
            element.allocatedBy = user && user.UserName ? `${userType} - ${user.UserName}` : `${userType} - Unknown`;
          }),
          catchError((error) => {
            console.error('Error fetching broker user details:', error);
            return of(null); // Return a default observable on error
          })
        );
    } else {
      const user = this.cachedClientUser;
          element.allocatedBy = user && user.userID ? `Client - ${user.userID}` : '';
      return of(user);
    }
  }

  getActionString(element: AllocationLog): string {
    return element?.action == "1" ? 'Undone' : '';
  }

  handleError(error: any): Observable<null> {
    console.error('Error fetching user:', error);
    return of(null);
  }

  getPagedData(): void {
    this.pagedElements$ = of(this.allocationLogs.slice(this.pageSize * this.pageIndex, (this.pageSize * this.pageIndex) + this.pageSize));
  }

  pageChangeEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getPagedData();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
